import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Swords, Activity, Flame, Star, TrendingUp } from 'lucide-react';

const StatPill = ({ label, value, sub }) => (
  <div className="flex flex-col items-center justify-center bg-white/70 rounded-xl px-3 py-2 min-w-[80px]">
    <span className="text-2xl font-extrabold text-gray-900">{value}</span>
    <span className="text-[11px] text-gray-500 leading-tight text-center">{label}</span>
    {sub != null && <span className="text-[11px] font-semibold text-emerald-600">{sub}</span>}
  </div>
);

const AdvancedStats = ({ userId }) => {
  const { API } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/users/${userId}/advanced-stats`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, API]);

  if (loading || !data) return null;

  const { rivalry, set_stats: ss, personal_best: pb, heatmap } = data;
  const hasAny = rivalry || (ss && ss.total_sets > 0) || (pb && (pb.longest_win_streak > 0 || pb.max_points_tournament > 0)) || (heatmap && heatmap.length > 0);
  if (!hasAny) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="advanced-stats">
      {/* היריב הקבוע */}
      {rivalry && (
        <Card className="border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-white" data-testid="rivalry-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg text-rose-700">
              <Swords className="w-5 h-5" /> היריב הקבוע שלך
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-gray-900 mb-1">{rivalry.opponent_name}</div>
            <div className="text-sm text-gray-500 mb-3">{rivalry.matches} מפגשים</div>
            <div className="flex gap-3">
              <StatPill label="ניצחונות" value={rivalry.wins} />
              <StatPill label="הפסדים" value={rivalry.losses} />
              <StatPill label="מאזן" value={`${rivalry.wins}-${rivalry.losses}`} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* שיא אישי */}
      {pb && (pb.longest_win_streak > 0 || pb.max_points_tournament > 0) && (
        <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="personal-best-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg text-amber-700">
              <Star className="w-5 h-5" /> שיאים אישיים
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 bg-white/70 rounded-xl p-3">
              <Flame className="w-7 h-7 text-orange-500" />
              <div>
                <div className="text-2xl font-extrabold text-gray-900">{pb.longest_win_streak}</div>
                <div className="text-xs text-gray-500">רצף הניצחונות הארוך ביותר</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/70 rounded-xl p-3">
              <TrendingUp className="w-7 h-7 text-emerald-500" />
              <div>
                <div className="text-2xl font-extrabold text-gray-900">{pb.max_points_tournament} <span className="text-sm font-normal text-gray-500">נק׳</span></div>
                <div className="text-xs text-gray-500 truncate">שיא נקודות בתחרות{pb.best_points_tournament_name ? ` · ${pb.best_points_tournament_name}` : ''}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* סטטיסטיקת סטים */}
      {ss && ss.total_sets > 0 && (
        <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white" data-testid="set-stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg text-blue-700">
              <Activity className="w-5 h-5" /> סטטיסטיקת סטים
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              <StatPill label="ניצחון בסטים" value={`${ss.sets_win_rate}%`} sub={`${ss.sets_won}/${ss.total_sets}`} />
              <StatPill label="טייברייקים" value={`${ss.tiebreak_win_rate}%`} sub={`${ss.tiebreaks_won}/${ss.tiebreaks_played}`} />
              <StatPill label="סטים צמודים" value={`${ss.close_set_win_rate}%`} sub={`${ss.close_sets_won}/${ss.close_sets}`} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* מפת חום לפי קטגוריה */}
      {heatmap && heatmap.length > 0 && (
        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="heatmap-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg text-emerald-700">
              <Activity className="w-5 h-5" /> מפת ביצועים לפי קטגוריה
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {heatmap.map((c) => {
              const wr = c.win_rate;
              const bg = wr >= 70 ? 'bg-emerald-500' : wr >= 50 ? 'bg-blue-500' : wr >= 30 ? 'bg-amber-500' : 'bg-rose-400';
              return (
                <div key={c.category} className="flex items-center gap-3" data-testid={`heatmap-row-${c.category}`}>
                  <div className="w-24 text-sm font-medium text-gray-700 truncate">{c.category}</div>
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${bg} rounded-full flex items-center justify-end pl-2 transition-all`} style={{ width: `${Math.max(wr, 8)}%` }}>
                      <span className="text-[11px] font-bold text-white">{wr}%</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 w-16 text-left">{c.wins}-{c.losses}</div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdvancedStats;
